import { ApiError, getCsrfToken, resetCsrfToken } from '../api/client.ts';
import type { ApiErrorBody } from '../api/client.ts';

/**
 * Скачивает файл через серверный прокси `POST /api/import/fetch-remote`.
 * Прокси нужен из-за CORS на `docs.google.com` и для нормализации
 * share-ссылок Google Sheets/Drive в прямой `export?format=xlsx`.
 *
 * Возвращает `File`, чтобы выше по стеку UI-компонент работал с Google
 * Drive ровно так же, как с локальным файлом из `<input type="file">`.
 */
export async function fetchRemoteImportFile(url: string): Promise<File> {
  const doRequest = async (): Promise<Response> =>
    fetch('/api/import/fetch-remote', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': await getCsrfToken(),
      },
      body: JSON.stringify({ url }),
    });

  let res = await doRequest();
  if (res.status === 403) {
    // sliding-regeneration CSRF — единственный повтор, как в apiRequest
    resetCsrfToken();
    res = await doRequest();
  }

  if (!res.ok) {
    let body: { error?: ApiErrorBody } = {};
    try {
      body = await res.json();
    } catch {
      /* not json */
    }
    throw new ApiError(
      res.status,
      body.error ?? { code: 'fetch_failed', message: `HTTP ${res.status}` },
    );
  }

  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  const name = m?.[1]?.trim() ?? 'import.xlsx';
  return new File([blob], name, { type: blob.type || 'application/octet-stream' });
}
